@if (isLoading()) {
    <div class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Carregando...</span>
        </div>
    </div>
} @else {
    <!-- Header compacto -->
    <div class="campaign-header">

        <!-- Título + status -->
        <div class="d-flex align-items-center gap-2 flex-wrap">
            <button class="btn btn-sm btn-light p-0" (click)="goBack()" title="Voltar">
                <i class="ti ti-arrow-left fs-5"></i>
            </button>
            <h4 class="mb-0 title-truncate">{{ campaign()?.name }}</h4>

            <!-- Ícone de descrição com tooltip -->
            @if (campaign()?.description) {
                <i class="ti ti-info-circle fs-5 cursor-pointer"
                   [ngbTooltip]="campaign().description"></i>
            }

            <div class="d-flex align-items-center gap-2 ms-auto">
                <span class="badge" [class]="getStatusBadgeClass(campaign()?.status)">
                    {{ getStatusLabel(campaign()?.status) }}
                </span>
            </div>
        </div>

        <!-- Campaign Context (Filters) -->
        @if (campaign()?.context) {
            <div class="campaign-context mt-3 p-3 bg-light rounded">
                <div class="mb-2">
                    <small class="text-muted fw-semibold text-uppercase" style="font-size: 0.7rem; letter-spacing: 0.5px;">
                        <i class="ti ti-filter me-1"></i>
                        Filtros Aplicados
                    </small>
                </div>
                <div class="d-flex gap-2 flex-wrap">
                    @if (campaign().context.q) {
                        <span class="badge bg-primary-subtle text-primary border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-search me-1"></i>
                            Busca: "{{ campaign().context.q }}"
                        </span>
                    }

                    @if (campaign().context.state) {
                        <span class="badge bg-info-subtle text-info border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-map-pin me-1"></i>
                            {{ campaign().context.state }}
                        </span>
                    }

                    @if (campaign().context.city) {
                        <span class="badge bg-info-subtle text-info border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-building me-1"></i>
                            {{ campaign().context.city }}
                        </span>
                    }

                    @if (campaign().context.companySize) {
                        <span class="badge bg-secondary-subtle text-secondary border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-building-community me-1"></i>
                            Porte: {{ campaign().context.companySize }}
                        </span>
                    }

                    @if (campaign().context.registrationStatus) {
                        <span class="badge bg-secondary-subtle text-secondary border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-file-check me-1"></i>
                            {{ campaign().context.registrationStatus }}
                        </span>
                    }

                    @if (campaign().context.personType) {
                        <span class="badge bg-primary-subtle text-primary border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-user me-1"></i>
                            {{ PersonTypeHelper.getFullLabel(campaign().context.personType) }}
                        </span>
                    }

                    @if (campaign().context.minEstimatedDebt || campaign().context.maxEstimatedDebt) {
                        <span class="badge bg-success-subtle text-success border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-currency-real me-1"></i>
                            Dívida: {{ formatCurrencyValue(campaign().context.minEstimatedDebt) }} - {{ formatCurrencyValue(campaign().context.maxEstimatedDebt) }}
                        </span>
                    }

                    @if (campaign().context.minDebtInstallmentValue || campaign().context.maxDebtInstallmentValue) {
                        <span class="badge bg-success-subtle text-success border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-calendar-event me-1"></i>
                            Valor Parc.: {{ formatCurrencyValue(campaign().context.minDebtInstallmentValue) }} - {{ formatCurrencyValue(campaign().context.maxDebtInstallmentValue) }}
                        </span>
                    }

                    @if (campaign().context.minInstallmentRemaining || campaign().context.maxInstallmentRemaining) {
                        <span class="badge bg-warning-subtle text-warning border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-list-numbers me-1"></i>
                            Parc. Restantes: {{ campaign().context.minInstallmentRemaining || '0' }} - {{ campaign().context.maxInstallmentRemaining || '∞' }}
                        </span>
                    }

                    @if (campaign().context.minTotalInstallmentRemaining || campaign().context.maxTotalInstallmentRemaining) {
                        <span class="badge bg-warning-subtle text-warning border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-list-check me-1"></i>
                            Total Parc.: {{ campaign().context.minTotalInstallmentRemaining || '0' }} - {{ campaign().context.maxTotalInstallmentRemaining || '∞' }}
                        </span>
                    }

                    @if (campaign().context.startMonthInstallment || campaign().context.endMonthInstallment) {
                        <span class="badge bg-warning-subtle text-warning border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-calendar-month me-1"></i>
                            Período: {{ formatMonthYear(campaign().context.startMonthInstallment) }} - {{ formatMonthYear(campaign().context.endMonthInstallment) }}
                        </span>
                    }

                    @if (campaign().context.negotiationType && campaign().context.negotiationType.length > 0) {
                        <span class="badge bg-purple-subtle text-purple border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-file-text me-1"></i>
                            {{ campaign().context.negotiationType.length }} tipo(s) de negociação
                        </span>
                    }

                    @if (campaign().context.excludeRescindedTransacaoRepactuacaoUpTo26Overdues) {
                        <span class="badge bg-danger-subtle text-danger border-0" style="padding: 0.5rem 0.75rem; font-weight: 500;">
                            <i class="ti ti-ban me-1"></i>
                            Excluir rescindidas até 26 atrasos
                        </span>
                    }
                </div>
            </div>
        }

        <app-kanban-board
            [config]="kanbanConfig"
            [campaignId]="campaignId">
        </app-kanban-board>
    </div>
}
